import { useRef, useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  Flex,
  Grid,
  GridItem,
  Text,
  Tooltip
} from '@chakra-ui/react';
import {
  Accordion,
  Button,
  ButtonIcon,
  ButtonText,
  Input,
  Pagination,
  Select
} from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faPlus,
  faRefresh,
  faSearch,
  faTrashAlt,
  faUser
} from '@fortawesome/pro-regular-svg-icons';
import {
  faAngleDoubleDown,
  faAngleDoubleUp
} from '@fortawesome/pro-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { RecommendationProvider } from '@publishing-engine/domain/models';
import { fetchReviewerRecommendations } from 'src/api/queries';
import { useReviewerInviteList } from 'src/contexts/ReviewerInviteListProvider';
import Loading from 'src/pages/Status/Loading';
import { formatDate } from 'src/utils/date';
import SortMenu, {
  SortField
} from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Sort/SortMenu';
import ExternalRecommendationCard from './ExternalRecommendationCard';
import ExternalRecommendationDetailsDrawer from './ExternalRecommendationDetailsDrawer';
import NoSearchResultFound from '../NoSearchResultFound';
import { IExternalRecommendation } from './types';

interface Props {
  submissionContextId: string;
  submissionId: string;
  provider: RecommendationProvider;
  title: string;
  isOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
}

const PAGE_SIZE = 10;

const SORT_FIELDS: SortField[] = [
  { value: 'score', label: 'Score (High to Low)', direction: 'desc' },
  { value: 'score', label: 'Score (Low to High)', direction: 'asc' },
  { value: 'firstName', label: 'First Name (A–Z)', direction: 'asc' },
  { value: 'firstName', label: 'First Name (Z–A)', direction: 'desc' },
  { value: 'lastName', label: 'Last Name (A–Z)', direction: 'asc' },
  { value: 'lastName', label: 'Last Name (Z–A)', direction: 'desc' }
];

const DEFAULT_SORT: SortField = {
  value: 'score',
  label: 'Score (High to Low)',
  direction: 'desc'
};

type SearchField = 'firstName' | 'lastName' | 'email' | 'affiliation';

const SEARCH_FIELD_OPTIONS: { value: SearchField; label: string }[] = [
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'affiliation', label: 'Affiliation' }
];

type SearchCriterion = { id: number; field: SearchField; value: string };

const PROVIDER_NAMES: Record<RecommendationProvider, string> = {
  [RecommendationProvider.PROPHY]: 'Prophy',
  [RecommendationProvider.GLOBAL_CAMPUS]: 'Global Campus',
  [RecommendationProvider.WEB_OF_SCIENCE]: 'Web of Science'
};

const getProviderName = (provider: RecommendationProvider) =>
  PROVIDER_NAMES[provider] ?? provider;

const ExternalRecommendations = ({
  submissionContextId,
  submissionId,
  provider,
  title,
  isOpen,
  onToggle
}: Props) => {
  const { addReviewer, removeReviewersByEmails, reviewerList } =
    useReviewerInviteList();
  const [page, setPage] = useState(1);
  const [criteria, setCriteria] = useState<SearchCriterion[]>([
    { id: 0, field: 'firstName', value: '' }
  ]);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, string>>(
    {}
  );
  const [sort, setSort] = useState<SortField | null>(DEFAULT_SORT);
  const [detailsReviewer, setDetailsReviewer] =
    useState<IExternalRecommendation | null>(null);
  const [expandAll, setExpandAll] = useState<boolean>(true);
  const isReloadRef = useRef(false);
  const nextCriterionId = useRef(1);

  const offset = (page - 1) * PAGE_SIZE;
  const providerName = getProviderName(provider);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: [
      'reviewer-recommendations',
      provider,
      submissionContextId,
      submissionId,
      page,
      appliedFilters,
      sort
    ],
    queryFn: () =>
      fetchReviewerRecommendations({
        submissionContextId,
        submissionId,
        provider,
        offset,
        limit: PAGE_SIZE,
        filters: appliedFilters,
        sort: sort
          ? {
              value: sort.value as 'firstName' | 'lastName' | 'score',
              direction: sort.direction
            }
          : undefined,
        isReload: isReloadRef.current
      }),
    enabled: !!submissionContextId && !!submissionId,
    keepPreviousData: true,
    onSuccess: () => {
      isReloadRef.current = false;
    }
  });

  const reviewers = data?.reviewers ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const updatedAt = data?.updatedAt;

  const isReviewerSelected = (email?: string | null) =>
    !!email && reviewerList.some(r => r.email === email);

  const handleCheckboxChange = (reviewer: IExternalRecommendation) => {
    const { email } = reviewer;
    if (!email) return;

    if (isReviewerSelected(email)) {
      removeReviewersByEmails([email]);
      return;
    }

    addReviewer({
      email,
      source: 'search',
      meta: {
        name: reviewer.name,
        institute: reviewer.affiliation ?? undefined
      }
    });
  };

  const isReviewerInvited = (email?: string | null) =>
    !!email && reviewerList.some(r => r.email === email && r.invited);

  const handleRefetch = () => {
    isReloadRef.current = true;
    refetch();
  };

  const handleSearch = () => {
    const filters: Record<string, string> = {};
    criteria.forEach(criterion => {
      const value = criterion.value.trim();
      if (value) filters[criterion.field] = value;
    });
    setAppliedFilters(filters);
    setPage(1);
  };

  const handleReset = () => {
    setCriteria([{ id: nextCriterionId.current++, field: 'firstName', value: '' }]);
    setAppliedFilters({});
    setPage(1);
  };

  const addCriterion = () => {
    setCriteria(previous => {
      const used = new Set(previous.map(criterion => criterion.field));
      const nextField = SEARCH_FIELD_OPTIONS.find(
        option => !used.has(option.value)
      )?.value;
      return nextField
        ? [
            ...previous,
            { id: nextCriterionId.current++, field: nextField, value: '' }
          ]
        : previous;
    });
  };

  const removeCriterion = (id: number) =>
    setCriteria(previous => previous.filter(criterion => criterion.id !== id));

  const updateCriterion = (
    id: number,
    patch: Partial<Pick<SearchCriterion, 'field' | 'value'>>
  ) =>
    setCriteria(previous =>
      previous.map(criterion =>
        criterion.id === id ? { ...criterion, ...patch } : criterion
      )
    );

  const handleSortApply = (selected: SortField | null) => {
    setSort(selected);
    setPage(1);
  };

  const handleSortReset = () => {
    setSort(DEFAULT_SORT);
    setPage(1);
  };

  const usedFields = new Set(criteria.map(criterion => criterion.field));
  const canAdd = criteria.length < SEARCH_FIELD_OPTIONS.length;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box>
          <Loading />
        </Box>
      );
    }

    if (isError) {
      return (
        <NoSearchResultFound
          title="No data available"
          description={`Unable to load ${providerName} recommendations. Please try again.`}
        />
      );
    }

    if (!reviewers.length && !updatedAt) {
      return (
        <Flex
          direction="column"
          alignItems="center"
          gap={3}
          paddingY={8}
          textAlign="center"
        >
          <FontAwesomeIcon icon={faUser} fontSize={32} />
          <Text textStyle="textBody" maxWidth="420px">
            Get reviewer recommendations based on the manuscript title,
            abstract and authors.
          </Text>
          <Button
            variant="primary"
            onClick={handleRefetch}
            isLoading={isFetching}
            loadingText="Loading Recommendations…"
          >
            Get Recommendations
          </Button>
        </Flex>
      );
    }

    return (
      <Flex direction="column" gap={3}>
        <Flex direction="column" gap={2}>
          {criteria.map(criterion => (
            <Grid
              key={criterion.id}
              templateColumns="30% 1fr auto auto"
              columnGap={4}
              rowGap={2}
              alignItems="flex-start"
            >
              <GridItem colSpan={1}>
                <Select
                  aria-label="Search field"
                  value={criterion.field}
                  onChange={e =>
                    updateCriterion(criterion.id, {
                      field: e.target.value as SearchField
                    })
                  }
                >
                  {SEARCH_FIELD_OPTIONS.map(option => (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={
                        option.value !== criterion.field &&
                        usedFields.has(option.value)
                      }
                    >
                      {option.label}
                    </option>
                  ))}
                </Select>
              </GridItem>

              <GridItem colSpan={1}>
                <Input
                  name={`external-search-value-${criterion.id}`}
                  type="text"
                  value={criterion.value}
                  onChange={e =>
                    updateCriterion(criterion.id, { value: e.target.value })
                  }
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  placeholder="Search value…"
                />
              </GridItem>

              <GridItem>
                <ButtonIcon
                  ariaLabel="Add Field"
                  icon={<FontAwesomeIcon icon={faPlus} />}
                  onClick={addCriterion}
                  isDisabled={!canAdd}
                />
              </GridItem>

              <GridItem>
                <ButtonIcon
                  ariaLabel="Delete Field"
                  icon={<FontAwesomeIcon icon={faTrashAlt} />}
                  onClick={() => removeCriterion(criterion.id)}
                  isDisabled={criteria.length === 1}
                />
              </GridItem>
            </Grid>
          ))}
        </Flex>

        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <Flex alignItems="center" gap={3}>
            {updatedAt && (
              <Text textStyle="headingOverline">
                Last Fetched on {formatDate(new Date(updatedAt))}
              </Text>
            )}
            <ButtonText
              size="regular"
              variant="secondary"
              iconLeft={<FontAwesomeIcon icon={faRefresh} />}
              onClick={handleRefetch}
            >
              Re-fetch Reviewers
            </ButtonText>
          </Flex>
          <Flex alignItems="center" gap={2}>
            <Button
              variant="primary"
              iconLeft={<FontAwesomeIcon icon={faSearch} />}
              onClick={handleSearch}
            >
              Search
            </Button>
            <Button
              variant="secondary"
              iconLeft={<FontAwesomeIcon icon={faArrowsRotate} />}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Flex>
        </Flex>
        <Divider />

        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <Flex alignItems="center" gap={2}>
            <Text textStyle="textBody">
              Showing <strong>{total}</strong> Reviewers
            </Text>
            <Tooltip
              label={expandAll ? 'Collapse All' : 'Expand All'}
              placement="top"
              hasArrow
            >
              <Box>
                <ButtonIcon
                  ariaLabel={expandAll ? 'Collapse All' : 'Expand All'}
                  icon={
                    <FontAwesomeIcon
                      icon={!expandAll ? faAngleDoubleDown : faAngleDoubleUp}
                    />
                  }
                  onClick={() => setExpandAll(prev => !prev)}
                />
              </Box>
            </Tooltip>
          </Flex>
          <Box width={{ base: '100%', lg: 'auto' }}>
            <SortMenu
              appliedSort={sort}
              options={{ fields: SORT_FIELDS }}
              onApply={handleSortApply}
              onReset={handleSortReset}
            />
          </Box>
        </Flex>


        {isFetching ? (
          <Box>
            <Loading />
          </Box>
        ) : reviewers.length ? (
          reviewers.map(reviewer => {
            const email = reviewer.email;
            return (
              <ExternalRecommendationCard
                key={email ?? reviewer.id}
                reviewer={reviewer}
                providerName={providerName}
                isSelected={isReviewerSelected(email)}
                isInvited={isReviewerInvited(email)}
                expandAll={expandAll}
                onChange={handleCheckboxChange}
                onViewDetails={setDetailsReviewer}
              />
            );
          })
        ) : (
          <NoSearchResultFound
            title="No reviewers found"
            description="No reviewers match your search. Try a different query."
          />
        )}

        {totalPages > 1 && (
          <Flex justifyContent="center" py={4} key={`${totalPages}-${page}`}>
            <Pagination
              ariaLabel={`${providerName} recommendations pagination`}
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Flex>
        )}
      </Flex>
    );
  };

  return (
    <>
      <Accordion
        chakraAccordionProps={{
          allowMultiple: true,
          ...(isOpen !== undefined
            ? {
                index: isOpen ? [0] : [],
                onChange: (idx: number[]) => onToggle?.(idx.includes(0))
              }
            : { defaultIndex: [] }),
          width: '100%',
          background: 'surfacePrimaryFill'
        }}
        items={[
          {
            accordionItemProps: {
              marginBottom: 0.5
            },
            title: (
              <Flex alignItems="center" gap={2}>
                <Text textStyle="headingItem">{title}</Text>
                <Badge variant="solid">
                  <Text textStyle="textContext">External</Text>
                </Badge>
              </Flex>
            ),
            content: renderContent()
          }
        ]}
      />
      <ExternalRecommendationDetailsDrawer
        reviewer={detailsReviewer}
        onClose={() => setDetailsReviewer(null)}
      />
    </>
  );
};

export default ExternalRecommendations;
